import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Icon } from "@iconify/react";
import {
  fetchEventAvatarAiThemes,
  pollEventAvatarAiJob,
  requestEventAvatarAiSourceUploadUrl,
  submitEventAvatarAiJob,
  uploadAvatarAiSource,
} from "../api/avatarAi";
import { trackEvent } from "../analytics";
import cardsWhiteLogoUrl from "../assets/Logo - Cards - white.svg";
import {
  AvatarAiCaptureInstruction,
  AvatarAiCaptureUploadBadge,
  AvatarAiCountdownOverlay,
  AvatarAiJobTile,
  AvatarAiPlayfulLoader,
  AvatarAiThemeSkeleton,
} from "./avatarAiShared";
import { useAvatarAiCameraCapture } from "../hooks/useAvatarAiCameraCapture";
import {
  AVATAR_AI_ACCEPTED_TYPES,
  AVATAR_AI_JOB_STATUS,
  AVATAR_AI_MAX_FILE_BYTES,
  AVATAR_AI_POLL_INTERVAL_MS,
  AVATAR_AI_POLL_MAX_MS,
  AVATAR_AI_QUIP_ROTATE_MS,
  buildAvatarAiQuipsForTheme,
  getAvatarAiFileSizeBucket,
  isAvatarAiJobTerminal,
} from "../lib/avatarAiFlow";
import {
  buildEventAvatarAiSignupHandoff,
  saveEventAvatarAiSignupHandoff,
} from "../lib/eventAvatarAiSignupHandoff";
import {
  avatarAiPreviewGenderOptions,
  getAvatarAiThemeDescription,
  getAvatarAiThemeLabel,
  getAvatarAiThemeThumbnailUrl,
} from "../lib/avatarAiThemePreviews";
import { noIndexSeo, setPageSeo } from "../seo";

const EVENT_SESSION_STORAGE_KEY = "cardsEventAvatarAiSessionId";
const PHOTO_AI_CREDITS = 3;
const EVENT_THEME_PRIORITY = [
  "linkedin-premium",
  "funeral-director",
  "studio-glamour",
  "pixar-3d",
  "roman-statue",
  "caricature",
];

const PAGE_STEPS = {
  CAMERA: "CAMERA",
  REVIEW: "REVIEW",
  GENERATING: "GENERATING",
  RESULTS: "RESULTS",
};

const createSessionId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `totem-${crypto.randomUUID()}`;
  }

  return `totem-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getEventSessionId = () => {
  if (typeof window === "undefined") return createSessionId();

  const existing = window.sessionStorage.getItem(EVENT_SESSION_STORAGE_KEY);
  if (existing) return existing;

  const next = createSessionId();
  window.sessionStorage.setItem(EVENT_SESSION_STORAGE_KEY, next);
  return next;
};

const trackEventAvatar = (name, props = {}) => {
  trackEvent(name, {
    category: "EventAvatarAi",
    ...props,
  });
};

const pickEventThemes = (themes = []) => {
  const byId = new Map(themes.map((theme) => [theme.id, theme]));
  const prioritized = EVENT_THEME_PRIORITY.map((id) => byId.get(id)).filter(
    Boolean,
  );
  const fallback = themes.filter(
    (theme) => !EVENT_THEME_PRIORITY.includes(theme.id),
  );

  return [...prioritized, ...fallback];
};

const EventAvatarAiPage = () => {
  const sessionId = useMemo(getEventSessionId, []);
  const [step, setStep] = useState(PAGE_STEPS.CAMERA);
  const [themes, setThemes] = useState([]);
  const [themesLoading, setThemesLoading] = useState(true);
  const [previewGender, setPreviewGender] = useState("male");
  const [selectedThemeIds, setSelectedThemeIds] = useState([]);
  const [capturePreview, setCapturePreview] = useState("");
  const [sourceKey, setSourceKey] = useState("");
  const [sourceAssetUrl, setSourceAssetUrl] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [creditsLeft, setCreditsLeft] = useState(PHOTO_AI_CREDITS);
  const [jobs, setJobs] = useState({});
  const [chosenThemeId, setChosenThemeId] = useState("");
  const [pollStartedAt, setPollStartedAt] = useState(0);
  const [pollNow, setPollNow] = useState(0);
  const [quipIndex, setQuipIndex] = useState(0);
  const [error, setError] = useState("");
  const autoStartAttemptedRef = useRef(false);
  const generationCompletedTrackedRef = useRef(false);
  const eventVideoConstraints = useMemo(
    () => ({
      facingMode: "user",
      width: { ideal: 1440 },
      height: { ideal: 1920 },
    }),
    [],
  );

  const handleCapturedFile = useCallback(
    async (file) => {
      if (!file) return;

      setError("");
      if (!AVATAR_AI_ACCEPTED_TYPES.includes(file.type)) {
        setError("Formato non supportato. Usa JPG, PNG o WebP.");
        return;
      }

      if (file.size > AVATAR_AI_MAX_FILE_BYTES) {
        setError("Immagine troppo grande. Il limite è 5 MB.");
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setCapturePreview(previewUrl);
      setUploadingPhoto(true);
      setUploadProgress(0);
      setSourceKey("");
      setSourceAssetUrl("");
      setJobs({});
      setChosenThemeId("");
      setCreditsLeft(PHOTO_AI_CREDITS);
      setSelectedThemeIds([]);

      trackEventAvatar("event_avatar_photo_captured", {
        sessionId,
        fileType: file.type || "unknown",
        fileSizeBucket: getAvatarAiFileSizeBucket(file.size || 0),
      });

      try {
        const descriptor = await requestEventAvatarAiSourceUploadUrl({
          sessionId,
          file,
        });
        await uploadAvatarAiSource({
          uploadDescriptor: descriptor,
          file,
          onProgress: setUploadProgress,
        });
        setSourceKey(descriptor.key);
        setSourceAssetUrl(descriptor.assetUrl || "");
        setStep(PAGE_STEPS.REVIEW);
        trackEventAvatar("event_avatar_photo_uploaded", {
          sessionId,
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setUploadingPhoto(false);
      }
    },
    [sessionId],
  );

  const {
    cameraActive,
    cameraAvailable,
    countdown,
    videoRef,
    canvasRef,
    startCamera,
    stopCamera,
    startCountdown,
  } = useAvatarAiCameraCapture({
    enabled: true,
    onCapture: handleCapturedFile,
    onError: (err) => setError(err.message || "Camera non disponibile."),
    onTrack: trackEventAvatar,
    videoConstraints: eventVideoConstraints,
  });

  const eventThemes = useMemo(() => pickEventThemes(themes), [themes]);
  const selectedCount = selectedThemeIds.length;
  const generationLimit = Math.min(PHOTO_AI_CREDITS, creditsLeft);
  const canGenerate =
    step === PAGE_STEPS.REVIEW &&
    Boolean(sourceKey) &&
    selectedCount > 0 &&
    selectedCount <= generationLimit;

  const selectedJobs = useMemo(() => Object.values(jobs), [jobs]);
  const allTerminal =
    selectedJobs.length > 0 &&
    selectedJobs.every((job) => isAvatarAiJobTerminal(job.status));
  const completedCount = selectedJobs.filter(
    (job) => job.status === AVATAR_AI_JOB_STATUS.COMPLETED,
  ).length;
  const completedAvatarChoices = useMemo(
    () =>
      Object.entries(jobs)
        .map(([themeId, job]) => {
          if (job.status !== AVATAR_AI_JOB_STATUS.COMPLETED || !job.resultUrl) {
            return null;
          }

          const theme = themes.find((item) => item.id === themeId);

          return {
            ...job,
            themeId,
            label: getAvatarAiThemeLabel(theme, previewGender) || themeId,
          };
        })
        .filter(Boolean),
    [jobs, previewGender, themes],
  );
  const completedJobs = completedAvatarChoices;
  const quips = useMemo(
    () => buildAvatarAiQuipsForTheme(selectedThemeIds[0] || ""),
    [selectedThemeIds],
  );
  const fakeProgressPct = useMemo(() => {
    if (step !== PAGE_STEPS.GENERATING || !pollStartedAt) return 0;
    const elapsed = Math.max(0, pollNow - pollStartedAt);
    const asymptotic = 1 - Math.exp(-elapsed / 18000);
    return Math.min(95, Math.round(asymptotic * 100));
  }, [step, pollStartedAt, pollNow]);

  useEffect(() => {
    setPageSeo(noIndexSeo);
    trackEventAvatar("event_avatar_page_viewed", { sessionId });
  }, [sessionId]);

  useEffect(() => {
    let cancelled = false;

    const loadThemes = async () => {
      setThemesLoading(true);
      try {
        const data = await fetchEventAvatarAiThemes();
        if (cancelled) return;
        const nextThemes = data.themes || [];
        setThemes(nextThemes);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setThemesLoading(false);
      }
    };

    loadThemes();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (
      autoStartAttemptedRef.current ||
      step !== PAGE_STEPS.CAMERA ||
      !cameraAvailable ||
      cameraActive ||
      capturePreview
    ) {
      return;
    }

    autoStartAttemptedRef.current = true;
    startCamera();
  }, [cameraActive, cameraAvailable, capturePreview, startCamera, step]);

  useEffect(() => {
    return () => {
      if (capturePreview.startsWith("blob:")) {
        URL.revokeObjectURL(capturePreview);
      }
    };
  }, [capturePreview]);

  useEffect(() => {
    if (step !== PAGE_STEPS.GENERATING) return undefined;
    setQuipIndex(0);
    const id = setInterval(() => {
      setQuipIndex((index) => (index + 1) % quips.length);
    }, AVATAR_AI_QUIP_ROTATE_MS);
    return () => clearInterval(id);
  }, [step, quips.length]);

  useEffect(() => {
    if (step !== PAGE_STEPS.GENERATING) return undefined;
    const id = setInterval(() => setPollNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [step]);

  useEffect(() => {
    if (step !== PAGE_STEPS.GENERATING) return undefined;

    let active = true;
    const tick = async () => {
      if (!active) return;
      const pendingEntries = Object.entries(jobs).filter(
        ([, job]) => job.jobId && !isAvatarAiJobTerminal(job.status),
      );

      if (pendingEntries.length === 0) return;

      const updates = await Promise.allSettled(
        pendingEntries.map(([themeId, job]) =>
          pollEventAvatarAiJob({ sessionId, jobId: job.jobId }).then(
            (data) => ({
              themeId,
              data,
            }),
          ),
        ),
      );

      if (!active) return;

      setJobs((current) => {
        const next = { ...current };
        updates.forEach((result, index) => {
          const [themeId] = pendingEntries[index];
          const existing = next[themeId];
          if (!existing) return;
          if (result.status === "fulfilled") {
            const data = result.value.data;
            next[themeId] = {
              ...existing,
              status: data.status || existing.status,
              resultUrl: data.resultUrl || existing.resultUrl,
              errorMessage: data.errorMessage || existing.errorMessage,
            };
          } else {
            next[themeId] = {
              ...existing,
              errorMessage: result.reason?.message || existing.errorMessage,
            };
          }
        });
        return next;
      });

      const elapsed = Date.now() - pollStartedAt;
      if (elapsed > AVATAR_AI_POLL_MAX_MS) {
        setJobs((current) => {
          const next = { ...current };
          Object.keys(next).forEach((themeId) => {
            if (!isAvatarAiJobTerminal(next[themeId].status)) {
              next[themeId] = {
                ...next[themeId],
                status: AVATAR_AI_JOB_STATUS.FAILED,
                errorMessage: "Timeout AI.",
              };
            }
          });
          return next;
        });
        trackEventAvatar("event_avatar_generation_timeout", {
          sessionId,
          selectedCount,
          durationMs: elapsed,
        });
        return;
      }

      if (active) {
        setTimeout(tick, AVATAR_AI_POLL_INTERVAL_MS);
      }
    };

    const timer = setTimeout(tick, AVATAR_AI_POLL_INTERVAL_MS);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [jobs, pollStartedAt, selectedCount, sessionId, step]);

  useEffect(() => {
    if (
      step !== PAGE_STEPS.GENERATING ||
      generationCompletedTrackedRef.current ||
      !allTerminal
    ) {
      return;
    }

    generationCompletedTrackedRef.current = true;
    setStep(PAGE_STEPS.RESULTS);
    trackEventAvatar("event_avatar_generation_completed", {
      sessionId,
      selectedCount: selectedJobs.length,
      completedCount,
      failedCount: selectedJobs.filter(
        (job) => job.status === AVATAR_AI_JOB_STATUS.FAILED,
      ).length,
      durationMs: pollStartedAt ? Date.now() - pollStartedAt : null,
    });
  }, [
    allTerminal,
    completedCount,
    pollStartedAt,
    selectedJobs,
    sessionId,
    step,
  ]);

  const toggleTheme = (themeId) => {
    setSelectedThemeIds((current) => {
      if (current.includes(themeId)) {
        return current.filter((id) => id !== themeId);
      }

      if (current.length >= generationLimit) {
        return current;
      }

      return [...current, themeId];
    });
  };

  const resetForNewPhoto = useCallback(() => {
    stopCamera();
    setStep(PAGE_STEPS.CAMERA);
    setError("");
    setCapturePreview("");
    setSourceKey("");
    setSourceAssetUrl("");
    setUploadProgress(0);
    setUploadingPhoto(false);
    setCreditsLeft(PHOTO_AI_CREDITS);
    setJobs({});
    setChosenThemeId("");
    setPollStartedAt(0);
    setPollNow(0);
    generationCompletedTrackedRef.current = false;
    setSelectedThemeIds([]);
    autoStartAttemptedRef.current = false;
    window.setTimeout(() => startCamera(), 120);
  }, [startCamera, stopCamera]);

  const handleCaptureCta = () => {
    setError("");
    if (!cameraActive) {
      autoStartAttemptedRef.current = true;
      startCamera();
      return;
    }

    trackEventAvatar("event_avatar_capture_cta_clicked", { sessionId });
    startCountdown();
  };

  const handleGenerate = async () => {
    if (!canGenerate) return;

    setError("");
    setStep(PAGE_STEPS.GENERATING);
    generationCompletedTrackedRef.current = false;
    const requestedThemeIds = selectedThemeIds.slice(0, generationLimit);

    trackEventAvatar("event_avatar_generation_started", {
      sessionId,
      selectedCount: requestedThemeIds.length,
      themeIds: requestedThemeIds,
      creditsBefore: creditsLeft,
    });

    const submissions = await Promise.allSettled(
      requestedThemeIds.map((themeId) =>
        submitEventAvatarAiJob({
          sessionId,
          themeId,
          sourceKey,
          modelId: "model-a",
        }).then((response) => ({ themeId, jobId: response.jobId })),
      ),
    );

    const nextJobs = {};
    let acceptedCount = 0;
    const fatalErrors = [];

    submissions.forEach((result, index) => {
      const themeId = requestedThemeIds[index];
      if (result.status === "fulfilled") {
        nextJobs[themeId] = {
          jobId: result.value.jobId,
          status: AVATAR_AI_JOB_STATUS.IN_QUEUE,
          resultUrl: "",
          errorMessage: "",
        };
        acceptedCount += 1;
      } else {
        const message = result.reason?.message || "Submit fallito.";
        nextJobs[themeId] = {
          jobId: "",
          status: AVATAR_AI_JOB_STATUS.FAILED,
          resultUrl: "",
          errorMessage: message,
        };
        fatalErrors.push(`${themeId}: ${message}`);
      }
    });

    setJobs(nextJobs);

    if (acceptedCount === 0) {
      setError(fatalErrors[0] || "Nessun job accettato. Riprova fra poco.");
      setStep(PAGE_STEPS.REVIEW);
      return;
    }

    setCreditsLeft((current) => Math.max(0, current - acceptedCount));
    setPollStartedAt(Date.now());
    setPollNow(Date.now());
  };

  const handlePreviewGenderChange = (gender) => {
    setPreviewGender(gender);
    trackEventAvatar("event_avatar_preview_gender_selected", {
      sessionId,
      gender,
    });
  };

  const selectedResult =
    completedJobs.find((job) => job.jobId === chosenThemeId) ||
    completedJobs[0];

  const handleCreateCardClick = () => {
    const handoff = buildEventAvatarAiSignupHandoff({
      sessionId,
      avatars: completedAvatarChoices,
      selectedJobId: selectedResult?.jobId,
    });

    saveEventAvatarAiSignupHandoff(handoff);
    trackEventAvatar("event_avatar_create_card_clicked", {
      sessionId,
      avatarsCount: completedAvatarChoices.length,
      selectedThemeId: selectedResult?.themeId || "",
    });
  };

  return (
    <div className="min-h-screen bg-[#e8eef2] font-Poppins text-slate-950">
      <header className="bg-[#17465c] px-5 py-4 text-white shadow-lg shadow-slate-950/15">
        <div className="mx-auto flex max-w-[980px] items-center justify-between gap-4">
          <a href="/" className="flex items-center">
            <img src={cardsWhiteLogoUrl} alt="Cards" className="h-10 w-auto" />
          </a>
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs font-extrabold uppercase tracking-[0.12em] text-cyan-50 ring-1 ring-white/15">
            <Icon icon="mdi:camera-iris" className="text-lg text-[#e7ff74]" />
            Live avatar AI
          </span>
        </div>
      </header>

      <style>{`
        @keyframes avatarAiCaptureIn {
          0% { opacity: 0; transform: scale(1.08); filter: blur(6px); }
          60% { opacity: 1; filter: blur(0); }
          100% { opacity: 1; transform: scale(1); filter: blur(0); }
        }
        @keyframes avatarAiThemeIn {
          0% { opacity: 0; transform: translateY(14px) scale(0.94); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
      <main className="relative h-[calc(100vh-72px)] overflow-hidden bg-gradient-to-br from-[#164b66] via-[#1b6f84] to-[#2399a4] text-white">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "linear-gradient(90deg, rgba(255,255,255,0.16) 1px, transparent 1px), linear-gradient(0deg, rgba(255,255,255,0.12) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />
        <div className="absolute inset-x-0 bottom-0 h-20 bg-[#e8eef2]" />

        <div className="relative mx-auto flex h-full max-w-[980px] flex-col gap-4 px-5 py-4 sm:px-8">
          <section className="flex min-h-0 flex-1 flex-col gap-4">
            <div
              className="relative flex min-h-0 flex-col overflow-hidden rounded-lg bg-slate-950 shadow-2xl shadow-slate-950/35 ring-1 ring-white/15"
              style={{
                flexBasis: step === PAGE_STEPS.CAMERA ? "49%" : "32%",
              }}
            >
              <div className="relative min-h-0 flex-1 overflow-hidden">
                {capturePreview ? (
                  <img
                    src={capturePreview}
                    alt="Foto appena scattata"
                    className="h-full w-full object-cover object-center animate-[avatarAiCaptureIn_520ms_cubic-bezier(0.22,1,0.36,1)_both]"
                  />
                ) : cameraActive ? (
                  <video
                    ref={videoRef}
                    playsInline
                    muted
                    className="h-full w-full scale-x-[-1] object-contain"
                  />
                ) : (
                  <div className="flex h-full flex-col items-center justify-center bg-slate-950 px-8 text-center">
                    <Icon
                      icon={
                        cameraAvailable
                          ? "mdi:camera-iris"
                          : "mdi:camera-off-outline"
                      }
                      className="text-7xl text-cyan-100/60"
                    />
                    <p className="mt-5 text-2xl font-black text-white">
                      {cameraAvailable
                        ? "Fotocamera pronta"
                        : "Fotocamera non disponibile"}
                    </p>
                  </div>
                )}

                {cameraActive && !capturePreview && (
                  <AvatarAiCaptureInstruction
                    label="Guarda nell'obiettivo"
                    className="top-5 px-5 py-2 text-sm"
                  />
                )}
                <AvatarAiCountdownOverlay
                  countdown={countdown}
                  label="Guarda nell'obiettivo"
                  className="text-center"
                />
                {uploadingPhoto && (
                  <AvatarAiCaptureUploadBadge
                    progress={uploadProgress}
                    label="Preparo la foto..."
                  />
                )}

                <div className="absolute left-4 top-4 flex items-center gap-2 rounded-full bg-red-500 px-3 py-1.5 text-xs font-black uppercase tracking-[0.12em] text-white shadow-lg">
                  <span className="h-2 w-2 rounded-full bg-white" />
                  Live
                </div>
                <div className="absolute bottom-4 left-4 max-w-[78%] text-left">
                  <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#e7ff74] ring-1 ring-white/15">
                    <Icon icon="mdi:auto-fix" className="text-sm" />
                    Totem evento Cards
                  </p>
                  <h1 className="mt-2 text-2xl font-black leading-none text-white drop-shadow sm:text-4xl">
                    Scatta. Genera. Porta il tuo avatar su Cards.
                  </h1>
                </div>
                <canvas ref={canvasRef} className="hidden" />
              </div>

              <div className="grid gap-3 border-t border-white/10 bg-slate-950/92 p-4 sm:grid-cols-[1fr_auto] sm:items-center">
                <div>
                  <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-cyan-100/70">
                    {step === PAGE_STEPS.CAMERA
                      ? "Entra nell'inquadratura"
                      : step === PAGE_STEPS.REVIEW
                        ? "Foto pronta"
                        : step === PAGE_STEPS.GENERATING
                          ? "Avatar in generazione"
                          : "Avatar pronti"}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-cyan-50/80">
                    {step === PAGE_STEPS.CAMERA
                      ? "Il countdown scatta la foto in 3 secondi."
                      : step === PAGE_STEPS.REVIEW
                        ? "Hai 3 crediti AI per questa foto."
                        : step === PAGE_STEPS.GENERATING
                          ? `${completedCount}/${selectedCount} varianti pronte.`
                          : `${completedJobs.length} varianti generate.`}
                  </p>
                </div>
                <div className="flex flex-wrap justify-center gap-2 sm:justify-end">
                  {step === PAGE_STEPS.CAMERA && (
                    <button
                      type="button"
                      onClick={handleCaptureCta}
                      disabled={countdown !== null || uploadingPhoto}
                      className="inline-flex items-center justify-center gap-3 rounded-md bg-white px-6 py-4 text-base font-black text-[#13526f] shadow-lg shadow-black/20 transition hover:bg-cyan-50 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      <Icon
                        icon="mdi:camera-plus-outline"
                        className="text-2xl"
                      />
                      {cameraActive
                        ? countdown !== null
                          ? "Guarda qui..."
                          : "Crea il tuo avatar per Cards"
                        : "Accendi fotocamera"}
                    </button>
                  )}
                  {step !== PAGE_STEPS.CAMERA && (
                    <button
                      type="button"
                      onClick={resetForNewPhoto}
                      className="inline-flex items-center justify-center gap-2 rounded-md bg-white/10 px-5 py-3 text-sm font-extrabold text-white ring-1 ring-white/20 transition hover:bg-white/15"
                    >
                      <Icon
                        icon="mdi:camera-retake-outline"
                        className="text-xl"
                      />
                      Rifai foto
                    </button>
                  )}
                </div>
              </div>
            </div>

            <aside className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-white p-4 text-slate-950 shadow-xl shadow-slate-950/20 ring-1 ring-white/70">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-[#13526f]">
                    Crediti foto
                  </p>
                  <p className="mt-1 text-4xl font-black leading-none text-slate-950">
                    {creditsLeft}/{PHOTO_AI_CREDITS}
                  </p>
                </div>
                <div className="flex h-14 w-14 items-center justify-center rounded-md bg-cyan-50 text-[#1b8395]">
                  <Icon icon="mdi:creation-outline" className="text-3xl" />
                </div>
              </div>

              {step === PAGE_STEPS.REVIEW && (
                <div className="relative mt-4 flex min-h-0 flex-1 flex-col overflow-y-auto pr-1">
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <p className="text-xs font-extrabold uppercase tracking-[0.1em] text-slate-500">
                        Scegli i tuoi 3 stili
                      </p>
                      <p className="mt-1 text-xs font-semibold text-slate-500">
                        Tocca le card per selezionare ({selectedCount}/
                        {generationLimit}).
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <div className="grid grid-cols-2 rounded-full bg-slate-100 p-1 text-xs font-black text-slate-500 ring-1 ring-slate-200">
                        {avatarAiPreviewGenderOptions.map((option) => (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => handlePreviewGenderChange(option.id)}
                            className={`rounded-full px-3 py-1 transition ${
                              previewGender === option.id
                                ? "bg-[#13526f] text-white shadow-sm"
                                : "hover:text-slate-800"
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                      <span className="rounded-full bg-cyan-50 px-3 py-1 text-xs font-black text-[#13526f]">
                        {eventThemes.length} stili
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 grid auto-rows-min grid-cols-2 content-start gap-3 pb-24 sm:grid-cols-3">
                    {themesLoading &&
                      Array.from({ length: 6 }).map((_, index) => (
                        <AvatarAiThemeSkeleton key={`event-theme-${index}`} />
                      ))}
                    {!themesLoading &&
                      eventThemes.map((theme, idx) => {
                        const isSelected = selectedThemeIds.includes(theme.id);
                        const disabled =
                          !isSelected &&
                          selectedThemeIds.length >= generationLimit;
                        const thumbnailUrl = getAvatarAiThemeThumbnailUrl(
                          theme,
                          previewGender,
                        );
                        const themeLabel = getAvatarAiThemeLabel(
                          theme,
                          previewGender,
                        );
                        const themeDescription = getAvatarAiThemeDescription(
                          theme,
                          previewGender,
                        );
                        return (
                          <button
                            key={theme.id}
                            type="button"
                            disabled={disabled}
                            onClick={() => toggleTheme(theme.id)}
                            style={{
                              animation: `avatarAiThemeIn 480ms cubic-bezier(0.22,1,0.36,1) ${idx * 55}ms both`,
                            }}
                            className={`overflow-hidden rounded-lg border text-left transition ${
                              isSelected
                                ? "border-[#1b8395] ring-2 ring-cyan-200 scale-[1.02]"
                                : disabled
                                  ? "border-slate-200 opacity-45"
                                  : "border-slate-200 hover:border-cyan-300"
                            }`}
                          >
                            <div
                              className="relative w-full bg-slate-100"
                              style={{ aspectRatio: "3 / 4" }}
                            >
                              {thumbnailUrl ? (
                                <img
                                  src={thumbnailUrl}
                                  alt={themeLabel}
                                  className="absolute inset-0 h-full w-full object-contain"
                                />
                              ) : (
                                <div className="flex h-full items-center justify-center text-slate-300">
                                  <Icon
                                    icon="mdi:image-outline"
                                    className="text-5xl"
                                  />
                                </div>
                              )}
                              <span
                                className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.08em] ${
                                  theme.type === "pro"
                                    ? "bg-emerald-600 text-white"
                                    : "bg-fuchsia-600 text-white"
                                }`}
                              >
                                {theme.type === "pro" ? "Pro" : "Fun"}
                              </span>
                              {isSelected && (
                                <span className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-[#1b8395] text-white shadow-lg shadow-cyan-950/20">
                                  <Icon icon="mdi:check" className="text-xl" />
                                </span>
                              )}
                            </div>
                            <div className="min-w-0 bg-white p-4">
                              <div className="flex items-center gap-2">
                                <p className="truncate text-base font-black text-slate-950">
                                  {themeLabel}
                                </p>
                                {isSelected && (
                                  <Icon
                                    icon="mdi:check-circle"
                                    className="shrink-0 text-xl text-[#1b8395]"
                                  />
                                )}
                              </div>
                              <p className="mt-1 line-clamp-2 text-sm font-semibold leading-6 text-slate-500">
                                {themeDescription}
                              </p>
                            </div>
                          </button>
                        );
                      })}
                  </div>
                  <div className="sticky bottom-0 left-0 right-0 -mx-1 mt-4 bg-gradient-to-t from-white via-white/95 to-white/0 pb-1 pt-6 backdrop-blur">
                    <button
                      type="button"
                      onClick={handleGenerate}
                      disabled={!canGenerate}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#1b8395] px-5 py-4 text-sm font-black text-white shadow-lg shadow-cyan-950/15 transition hover:bg-[#13526f] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
                    >
                      <Icon icon="mdi:auto-fix" className="text-xl" />
                      {selectedCount > 0
                        ? `Genera ${selectedCount} avatar`
                        : "Scegli almeno uno stile"}
                    </button>
                  </div>
                </div>
              )}

              {step === PAGE_STEPS.GENERATING && (
                <div className="mt-4 grid min-h-0 flex-1 gap-4 lg:grid-cols-[320px_1fr]">
                  <div className="min-h-0">
                    <AvatarAiPlayfulLoader
                      phase="run"
                      quip={quips[quipIndex] || "Creo il tuo avatar..."}
                      sourceSrc={sourceAssetUrl || capturePreview}
                      progressPct={fakeProgressPct}
                      themeLabel="Avatar AI"
                    />
                  </div>
                  <div className="grid min-h-0 auto-rows-min grid-cols-3 content-start gap-3 overflow-y-auto pr-1">
                    {selectedThemeIds.map((themeId) => {
                      const theme = themes.find((item) => item.id === themeId);
                      const job = jobs[themeId];
                      return (
                        <AvatarAiJobTile
                          key={themeId}
                          label={
                            getAvatarAiThemeLabel(theme, previewGender) ||
                            themeId
                          }
                          type={theme?.type}
                          job={job}
                          selected={false}
                          aspectRatio="3 / 4"
                          imageFitClass="object-contain"
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {step === PAGE_STEPS.RESULTS && (
                <div className="mt-4 grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(260px,360px)_1fr]">
                  {selectedResult ? (
                    <>
                      <div className="min-h-0 overflow-hidden rounded-lg bg-slate-100 ring-1 ring-slate-200">
                        <img
                          src={selectedResult.resultUrl}
                          alt="Avatar AI generato"
                          className="h-full max-h-[34vh] w-full object-contain"
                        />
                      </div>
                      <div className="min-h-0">
                        <div className="grid max-h-[28vh] grid-cols-3 gap-3 overflow-y-auto pr-1">
                          {completedJobs.map((job) => (
                            <button
                              key={job.jobId}
                              type="button"
                              onClick={() => setChosenThemeId(job.jobId)}
                              className={`overflow-hidden rounded-md border ${
                                selectedResult.jobId === job.jobId
                                  ? "border-[#1b8395] ring-2 ring-cyan-200"
                                  : "border-slate-200"
                              }`}
                            >
                              <img
                                src={job.resultUrl}
                                alt=""
                                className="w-full bg-slate-100 object-contain"
                                style={{ aspectRatio: "3 / 4" }}
                              />
                            </button>
                          ))}
                        </div>
                        <a
                          href="/?coupon=TANEXPO26&avatarAi=totem#iscriviti"
                          onClick={handleCreateCardClick}
                          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#e7ff74] px-5 py-4 text-sm font-black text-[#073f35] shadow-lg shadow-emerald-950/10 transition hover:bg-lime-100"
                        >
                          <Icon
                            icon="mdi:card-account-details-star-outline"
                            className="text-xl"
                          />
                          Crea la tua Cards
                        </a>
                      </div>
                    </>
                  ) : (
                    <div className="rounded-lg bg-amber-50 p-4 text-sm font-bold text-amber-900 ring-1 ring-amber-200">
                      Nessuna variante pronta. Rifai la foto e riprova.
                    </div>
                  )}
                </div>
              )}

              {step === PAGE_STEPS.CAMERA && (
                <div className="mt-4 flex min-h-0 flex-1 flex-col items-center justify-center gap-5 rounded-lg bg-gradient-to-br from-cyan-50 via-white to-fuchsia-50 p-6 text-center ring-1 ring-cyan-100">
                  <div className="relative flex h-24 w-24 items-center justify-center">
                    <span className="absolute inset-0 animate-ping rounded-full bg-cyan-200/60" />
                    <span className="absolute inset-2 rounded-full bg-white shadow-lg shadow-cyan-200/50" />
                    <Icon
                      icon="mdi:camera-iris"
                      className="relative text-5xl text-[#1b8395]"
                    />
                  </div>
                  <div>
                    <p className="text-base font-black uppercase tracking-[0.12em] text-[#13526f]">
                      Pronti, scatta!
                    </p>
                    <p className="mt-2 max-w-sm text-sm font-semibold leading-6 text-slate-600">
                      Posa al centro, premi il bottone, parte il countdown da 3.
                      Subito dopo scegli i tuoi 3 stili AI preferiti.
                    </p>
                  </div>
                  <div className="grid w-full gap-3 text-sm font-semibold leading-6 text-slate-600 sm:grid-cols-2">
                    <div className="rounded-lg bg-white p-4 text-[#13526f] ring-1 ring-cyan-100">
                      <Icon
                        icon="mdi:numeric-3-circle-outline"
                        className="mr-2 inline text-xl"
                      />
                      3 crediti AI per ogni foto.
                    </div>
                    <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
                      <Icon
                        icon="mdi:camera-timer-outline"
                        className="mr-2 inline text-xl text-[#1b8395]"
                      />
                      Avatar pronti in pochi secondi.
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm font-bold text-red-700 ring-1 ring-red-100">
                  {error}
                </div>
              )}
            </aside>
          </section>
        </div>
      </main>
    </div>
  );
};

export default EventAvatarAiPage;
